/**
 * tenant.store — store Zustand do tenant ativo (multi-tenancy).
 *
 * Spec §8 (State Management) / §10 (TenantSwitcher):
 *   • `activeTenant`       → tenant selecionado no momento; cabeçalho `X-Tenant-ID`
 *                            é injetado automaticamente pelo Axios interceptor.
 *   • `accessibleTenants`  → lista de tenants aos quais o usuário corrente tem
 *                            acesso (vinda de /me ou /tenants).
 *   • `setActiveTenant`    → troca o tenant e persiste o `id` em localStorage
 *                            como `last_tenant_id` para reuso entre sessões.
 *
 * A invalidação de queries no `queryClient` ao trocar de tenant é tratada na
 * camada superior (componente TenantSwitcher) — este store é puro.
 */
import { create } from 'zustand'

import { STORAGE_KEYS } from '@/lib/constants'
import type { Tenant } from '@/types/api'

export interface TenantState {
  activeTenant: Tenant | null
  accessibleTenants: Tenant[]
}

export interface TenantActions {
  /** Define o tenant ativo e persiste `last_tenant_id` em localStorage. */
  setActiveTenant: (tenant: Tenant) => void
  /** Substitui a lista de tenants acessíveis (chamado após /me ou /tenants). */
  setAccessibleTenants: (tenants: Tenant[]) => void
  /** Limpa estado de tenant (chamado em logout). */
  clearTenant: () => void
}

export type TenantStore = TenantState & TenantActions

function writeLastTenantId(id: string | null): void {
  try {
    if (id === null) window.localStorage.removeItem(STORAGE_KEYS.TENANT)
    else window.localStorage.setItem(STORAGE_KEYS.TENANT, id)
  } catch {
    // localStorage indisponível — ignora silenciosamente.
  }
}

export const useTenantStore = create<TenantStore>((set) => ({
  activeTenant: null,
  accessibleTenants: [],

  setActiveTenant: (tenant) => {
    writeLastTenantId(tenant.id)
    set({ activeTenant: tenant })
  },

  setAccessibleTenants: (tenants) => set({ accessibleTenants: tenants }),

  clearTenant: () => {
    writeLastTenantId(null)
    set({ activeTenant: null, accessibleTenants: [] })
  },
}))

/**
 * Lê o `last_tenant_id` persistido — útil para a camada de bootstrap que,
 * após carregar `accessibleTenants`, pode restaurar o tenant da sessão anterior.
 *
 * Mantido como helper exportado (e não como estado inicial) para não tomar
 * decisões implícitas: a UI sabe quando é seguro restaurar a seleção.
 */
export function getPersistedTenantId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEYS.TENANT)
  } catch {
    return null
  }
}
